import { useState, useCallback, useEffect } from 'react';
import { SelectItemProps, Response } from '@ui/src';
import { useLazyGetPspPartnersOrganizationsQuery } from '../store/services';
import { PspPartnersOrganization } from '../types';

interface ListResponse {
  data: Response<PspPartnersOrganization>;
}

let timeout: NodeJS.Timeout;

const useGetOrganizations = () => {
  const [autocompleteList, setAutocompleteList] = useState<SelectItemProps[]>(
    [],
  );
  const [searchValue, setSearchValue] = useState('');
  const [selectedValue, setSelectedValue] = useState('');

  const [getList, { isFetching, isLoading }] =
    useLazyGetPspPartnersOrganizationsQuery();

  const getOrganizationById = useCallback(async (id: string) => {
    const results: SelectItemProps[] = [];

    const response = (await getList(`&ids=${id}&all=true`)) as ListResponse;

    if (response?.data?.data) {
      response.data.data.edges?.forEach((item) => {
        results.push({
          name: item.node?.firstName
            ? `${item.node.firstName} ${item.node?.lastName}`
            : (item?.node?.companyName as string),
          description: `${item?.node?.type.charAt(0)}${item?.node?.type.slice(1).toLowerCase()}`,
          value: item.node.id,
        });
      });

      setSelectedValue(id);
    }

    setAutocompleteList(results);
  }, []);

  const getAutocompleteListAction = useCallback(async () => {
    const results: SelectItemProps[] = [];

    const response = (await getList('')) as ListResponse;

    if (response?.data?.data) {
      response.data.data.edges?.forEach((item) => {
        results.push({
          name: item.node?.firstName
            ? `${item.node.firstName} ${item.node?.lastName}`
            : (item?.node?.companyName as string),
          description: `${item?.node?.type.charAt(0)}${item?.node?.type.slice(1).toLowerCase()}`,
          value: item.node.id,
        });
      });
    }

    setAutocompleteList(results);
  }, []);

  const onSearch = (search: string) => {
    clearTimeout(timeout);
    setSearchValue(search);

    timeout = setTimeout(async () => {
      if (search) {
        const results: SelectItemProps[] = [];

        const nameResponse = (await getList(
          `search=${search}`,
        )) as ListResponse;

        if (nameResponse?.data?.data) {
          nameResponse.data.data.edges?.forEach((item) => {
            if (
              !results?.find(
                (organization) => organization.value === item.node.id,
              )
            ) {
              results.push({
                name: `${item.node.firstName || ''} ${item.node?.lastName || ''}`,
                description: `${item?.node?.type.charAt(0)}${item?.node?.type.slice(1).toLowerCase()}`,
                value: item.node.id,
              });
            }
          });
        }

        const companyNameResponse = (await getList(
          `companyName=${search}`,
        )) as ListResponse;

        if (companyNameResponse?.data?.data) {
          companyNameResponse.data.data.edges?.forEach((item) => {
            if (
              !results?.find(
                (organization) => organization.value === item.node.id,
              )
            ) {
              results.push({
                name: item?.node?.companyName as string,
                description: `${item?.node?.type.charAt(0)}${item?.node?.type.slice(1).toLowerCase()}`,
                value: item.node.id,
              });
            }
          });
        }
        setAutocompleteList(results);
      } else {
        getAutocompleteListAction();
      }
    }, 1000);
  };

  const onClear = () => {
    getAutocompleteListAction();

    if (searchValue) {
      setSearchValue('');
    }

    if (selectedValue) {
      setSelectedValue('');
    }
  };

  useEffect(() => {
    getAutocompleteListAction();
  }, [getAutocompleteListAction]);

  return {
    getAutocompleteListAction,
    autocompleteList,
    onSearch,
    isLoading: isLoading || isFetching,
    onClear,
    getOrganizationById,
    selectedValue,
    setSelectedValue,
  };
};

export default useGetOrganizations;
